/**
 * Component for managing product pricing rules
 * Changes are made locally and saved via parent form's Save button
 *
 * Duration labels are auto-generated from duration_type + duration_value
 * using i18n locale strings, with translations auto-generated for all
 * merchant languages. Merchants can override with custom labels + translations.
 */

import { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../lib/i18n';
import {
  Plus,
  Trash2,
  X,
  Loader2,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Edit,
  Check,
  Pencil,
  Undo2
} from 'lucide-react';
import {
  getPricingRulesForProduct,
  getPricingRuleTranslationsBatch,
  type PricingRuleInput
} from '../services/pricingRules.service';

const CHANNELS = [
  { value: 'online', label: 'Online bookings' },
  { value: 'admin', label: 'Admin / manual bookings' }
];

const FLOW_TYPES = [
  { value: '', label: 'Both flows' },
  { value: 'immediate', label: 'Start Now' },
  { value: 'reservation', label: 'Reservation' }
];

interface PricingRuleChange extends Partial<PricingRuleInput> {
  id?: string; // undefined for new rules
  _status: 'existing' | 'added' | 'removed';
  _customLabel?: boolean;
  _translations?: Record<string, { duration_label: string }>;
}

export interface PricingRulesEditorRef {
  /** Auto-apply any in-progress rule edit and return the final rules array */
  flushEditing: () => PricingRuleChange[] | null;
}

interface LanguageOption {
  code: string;
  label: string;
  flag: string;
}

interface Props {
  productTypeId: string;
  currency: string;
  locations: Array<{ id: string; location_name: string }>;
  productSizes?: string[];
  onChange: (rules: PricingRuleChange[]) => void;
  availableLanguages?: LanguageOption[];
  defaultLanguage?: string;
}

const DURATION_TYPES = [
  { value: 'hours', label: 'Hours' },
  { value: 'calendar_day', label: 'Calendar Day' },
  { value: 'multi_day', label: 'Multi-Day' },
  { value: 'week', label: 'Week' },
];

const DAYS_OF_WEEK = [
  { value: 0, label: 'Sun' },
  { value: 1, label: 'Mon' },
  { value: 2, label: 'Tue' },
  { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' },
  { value: 5, label: 'Fri' },
  { value: 6, label: 'Sat' }
];

const DURATION_TYPE_KEYS: Record<string, string> = {
  hours: 'hours',
  until_closing: 'until_closing',
  calendar_day: 'calendar_day',
  multi_day: 'multi_day',
  week: 'week',
  custom: 'custom'
};

const DAY_KEYS: Record<number, string> = {
  0: 'sun',
  1: 'mon',
  2: 'tue',
  3: 'wed',
  4: 'thu',
  5: 'fri',
  6: 'sat'
};

const CHANNEL_KEYS: Record<string, string> = {
  online: 'onlineBookings',
  admin: 'adminBookings'
};

interface EditingRule extends Partial<PricingRuleInput> {
  id?: string;
  _status?: 'existing' | 'added' | 'removed';
  _customLabel?: boolean;
  _translations?: Record<string, { duration_label: string }>;
  /** The duration the current custom labels were written for. */
  _labelDuration?: { type?: string; value?: number | null };
  /** Custom labels that no longer describe the rule's duration. */
  _staleLangs?: string[];
}

/**
 * Read one locale string straight out of the resource store.
 *
 * Duration labels are customer-facing content in the *merchant's* languages,
 * not admin UI chrome, so they must not go through i18n.t(): t() resolves via
 * supportedLngs and the fallback chain, which silently yields English for any
 * language the admin UI does not itself ship. getResource() reads only the
 * requested language's bundle, so a locale can supply label data without
 * becoming a selectable admin UI language.
 */
function localeString(langCode: string, key: string): string | undefined {
  const value = i18n.getResource(langCode, 'translation', key);
  return typeof value === 'string' ? value : undefined;
}

/**
 * Generate a duration label from type + value using i18n locale strings.
 * Handles singular/plural: "1 Hour" vs "2 Hours", "1 Day" vs "3 Days", etc.
 * calendar_day and multi_day both produce "X Day(s)" labels.
 *
 * Callers must gate non-default languages on hasGeneratedLabels() — the t()
 * fallback below exists only so the merchant's own default language still
 * renders something if its bundle is incomplete.
 */
function generateDurationLabel(durationType: string, durationValue: number | null | undefined, langCode: string): string {
  const isSingular = durationValue === 1;

  const label = (key: string) => localeString(langCode, key) ?? i18n.t(key, { lng: langCode });

  // calendar_day and multi_day both use the same "day/days" labels
  if (durationType === 'calendar_day' || durationType === 'multi_day') {
    const typeLabel = label(isSingular ? 'pricingRules.label_day' : 'pricingRules.label_days');
    return durationValue ? `${durationValue} ${typeLabel}` : typeLabel;
  }

  if (durationType === 'hours') {
    const typeLabel = label(isSingular ? 'pricingRules.label_hour' : 'pricingRules.label_hours');
    return durationValue ? `${durationValue} ${typeLabel}` : typeLabel;
  }

  if (durationType === 'week') {
    const typeLabel = label(isSingular ? 'pricingRules.label_week' : 'pricingRules.label_weeks');
    return durationValue ? `${durationValue} ${typeLabel}` : typeLabel;
  }

  // Fallback for until_closing, custom, or unknown types
  const typeKey = DURATION_TYPE_KEYS[durationType];
  const typeLabel = typeKey ? label(`pricingRules.${typeKey}`) : durationType;
  return durationValue ? `${durationValue} ${typeLabel}` : typeLabel;
}

/**
 * Check if a stored duration_label matches what auto-generation would produce.
 * Also checks against the old raw format (e.g. "2 hours") for backward compatibility.
 */
function isAutoGeneratedLabel(
  label: string | undefined,
  durationType: string | undefined,
  durationValue: number | null | undefined,
  defaultLang: string
): boolean {
  if (!label || !durationType) return true;
  // Check against new localized format
  const localizedLabel = generateDurationLabel(durationType, durationValue, defaultLang);
  if (label === localizedLabel) return true;
  // Check against old raw format for backward compatibility
  const rawLabel = durationValue ? `${durationValue} ${durationType}` : durationType;
  if (label === rawLabel) return true;
  return false;
}

/**
 * Whether the admin UI locale bundle can actually produce duration labels for
 * a language.
 *
 * Merchants may offer far more content languages (LANGUAGE_CONFIGS) than the
 * admin panel ships UI locales for (src/locales/*.json). For an uncovered
 * language i18next silently resolves to the English fallback, so an
 * auto-generated label would be an English string masquerading as a
 * translation — and persisting it overwrites whatever the merchant really has.
 *
 * Note: i18n.exists() walks the fallback chain and returns true for every
 * language, so it cannot be used here.
 */
function hasGeneratedLabels(langCode: string): boolean {
  return localeString(langCode, 'pricingRules.label_hours') !== undefined;
}

/**
 * Build translations map for all non-default languages using auto-generation.
 *
 * Languages without locale data are never auto-generated. Any translation the
 * merchant already has for them is carried through unchanged; when there is
 * none, the language is omitted entirely so nothing is written for it.
 */
function generateAutoTranslations(
  durationType: string,
  durationValue: number | null | undefined,
  languages: LanguageOption[],
  defaultLang: string,
  stored?: Record<string, { duration_label: string }>
): Record<string, { duration_label: string }> {
  const translations: Record<string, { duration_label: string }> = {};
  for (const lang of languages) {
    if (lang.code === defaultLang) continue;

    if (hasGeneratedLabels(lang.code)) {
      translations[lang.code] = {
        duration_label: generateDurationLabel(durationType, durationValue, lang.code),
      };
      continue;
    }

    const existing = stored?.[lang.code]?.duration_label;
    if (existing) {
      translations[lang.code] = { duration_label: existing };
    }
  }
  return translations;
}

export const PricingRulesEditor = forwardRef<PricingRulesEditorRef, Props>(function PricingRulesEditor(
  { productTypeId, currency, locations, productSizes, onChange, availableLanguages, defaultLanguage },
  ref
) {
  const { t } = useTranslation();
  const defLang = defaultLanguage || 'en';
  const languages = availableLanguages || [];
  const hasMultipleLanguages = languages.length > 1;

  /** Every language a custom label is authored in, default language included. */
  const labelLanguages = Array.from(new Set([defLang, ...languages.map(l => l.code)]));

  const [rules, setRules] = useState<PricingRuleChange[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingRule, setEditingRule] = useState<EditingRule | null>(null);
  const [expandedRules, setExpandedRules] = useState<Set<string>>(new Set());
  const [activeTranslationTab, setActiveTranslationTab] = useState(defLang);

  const staleLangs = editingRule?._staleLangs || [];

  /**
   * Apply a duration change to the rule being edited.
   *
   * Auto-generated labels need no special handling — they are regenerated from
   * the duration. Custom labels are the merchant's own words, so they are never
   * rewritten silently: they are flagged as no longer matching the duration and
   * the merchant regenerates or rewrites them explicitly. Changing the duration
   * back to what the labels were written for clears the flag again.
   */
  function applyDurationChange(editing: EditingRule, patch: Partial<EditingRule>) {
    const next = { ...editing, ...patch };
    const authored = editing._labelDuration;
    const matchesAuthored =
      next.duration_type === authored?.type &&
      (next.duration_value ?? null) === (authored?.value ?? null);

    setEditingRule({
      ...next,
      _staleLangs: !editing._customLabel || matchesAuthored ? [] : labelLanguages,
    });
  }

  /** Replace one language's custom label with the generated one. */
  function regenerateLabel(editing: EditingRule, langCode: string) {
    const label = generateDurationLabel(editing.duration_type || 'hours', editing.duration_value, langCode);
    const remaining = (editing._staleLangs || []).filter(code => code !== langCode);

    setEditingRule(langCode === defLang
      ? { ...editing, duration_label: label, _staleLangs: remaining }
      : {
          ...editing,
          _translations: { ...editing._translations, [langCode]: { duration_label: label } },
          _staleLangs: remaining,
        });
  }

  /** Regenerate every stale label that can be generated. */
  function regenerateAllLabels(editing: EditingRule) {
    const stale = editing._staleLangs || [];
    const regenerable = stale.filter(hasGeneratedLabels);
    if (!regenerable.length) return;

    const translations = { ...editing._translations };
    let label = editing.duration_label;
    for (const code of regenerable) {
      const generated = generateDurationLabel(editing.duration_type || 'hours', editing.duration_value, code);
      if (code === defLang) label = generated;
      else translations[code] = { duration_label: generated };
    }

    setEditingRule({
      ...editing,
      duration_label: label,
      _translations: translations,
      // Languages with no locale data cannot be generated and stay flagged
      // until the merchant rewrites them by hand.
      _staleLangs: stale.filter(code => !regenerable.includes(code)),
    });
  }

  /** A language the merchant has just rewritten is no longer stale. */
  function clearStale(editing: EditingRule, langCode: string): string[] {
    return (editing._staleLangs || []).filter(code => code !== langCode);
  }

  /**
   * Build the final PricingRuleChange from the current editingRule state.
   * Handles both auto-generated and custom duration labels + translations.
   */
  function buildRuleData(editing: EditingRule): PricingRuleChange {
    const isCustom = editing._customLabel;

    // Duration label for the main table (default language)
    const durationLabel = isCustom
      ? (editing.duration_label || generateDurationLabel(editing.duration_type || 'hours', editing.duration_value, defLang))
      : generateDurationLabel(editing.duration_type || 'hours', editing.duration_value, defLang);

    // Translations for non-default languages
    const translations = isCustom
      ? (editing._translations || {})
      : generateAutoTranslations(editing.duration_type || 'hours', editing.duration_value, languages, defLang, editing._translations);

    return {
      id: editing.id,
      product_type_id: productTypeId,
      duration_type: editing.duration_type,
      duration_value: editing.duration_value ?? null,
      duration_label: durationLabel,
      price: editing.price,
      currency: currency,
      size_specific: editing.size_specific ?? false,
      size: editing.size_specific ? editing.size : null,
      valid_days_of_week: editing.valid_days_of_week?.length ? editing.valid_days_of_week : null,
      valid_time_from: editing.valid_time_from || null,
      valid_time_until: editing.valid_time_until || null,
      valid_from: editing.valid_from || null,
      valid_to: editing.valid_to || null,
      display_order: editing.display_order ?? 0,
      featured: editing.featured ?? false,
      active: editing.active ?? true,
      channels: editing.channels?.length ? editing.channels : ['online', 'admin'],
      flow_type: editing.flow_type || null,
      location_ids: editing.location_ids?.length ? editing.location_ids : null,
      _status: editing._status || 'added',
      _customLabel: isCustom,
      _translations: translations,
    };
  }

  useImperativeHandle(ref, () => ({
    flushEditing: () => {
      if (!editingRule || !editingRule.duration_type || editingRule.price === undefined) {
        return null;
      }

      const ruleData = buildRuleData(editingRule);

      let updated: PricingRuleChange[];
      if (editingRule.id) {
        updated = rules.map(r => r.id === editingRule.id ? ruleData : r);
      } else {
        updated = [...rules, { ...ruleData, id: `temp-${Date.now()}` }];
      }

      setRules(updated);
      onChange(updated);
      setEditingRule(null);
      return updated;
    }
  }));

  useEffect(() => {
    loadRules();
  }, [productTypeId]);

  async function loadRules() {
    setLoading(true);
    setError(null);
    const { data, error: err } = await getPricingRulesForProduct(productTypeId);
    if (err) {
      setError(err);
    } else {
      // Batch-load all translations for existing rules
      const ruleIds = (data || []).map(r => r.id);
      const { data: translationsMap } = await getPricingRuleTranslationsBatch(ruleIds);

      const initialRules: PricingRuleChange[] = (data || []).map(rule => {
        const existingTranslations = translationsMap?.[rule.id] || {};
        const isCustom = !isAutoGeneratedLabel(rule.duration_label, rule.duration_type, rule.duration_value, defLang);

        return {
          id: rule.id,
          product_type_id: rule.product_type_id,
          duration_type: rule.duration_type,
          duration_value: rule.duration_value,
          duration_label: rule.duration_label,
          price: rule.price,
          currency: rule.currency,
          size_specific: rule.size_specific,
          size: rule.size,
          valid_days_of_week: rule.valid_days_of_week,
          valid_time_from: rule.valid_time_from,
          valid_time_until: rule.valid_time_until,
          valid_from: rule.valid_from,
          valid_to: rule.valid_to,
          display_order: rule.display_order,
          featured: rule.featured,
          active: rule.active,
          channels: rule.channels || ['online', 'admin'],
          flow_type: rule.flow_type || null,
          location_ids: rule.location_ids,
          _status: 'existing' as const,
          _customLabel: isCustom,
          _translations: isCustom
            ? existingTranslations
            : generateAutoTranslations(rule.duration_type, rule.duration_value, languages, defLang, existingTranslations),
        };
      });
      setRules(initialRules);
      onChange(initialRules);
    }
    setLoading(false);
  }

  function startNewRule() {
    setEditingRule({
      product_type_id: productTypeId,
      duration_type: 'hours',
      duration_value: 1,
      price: 0,
      currency: currency,
      size_specific: false,
      size: null,
      valid_days_of_week: null,
      valid_time_from: null,
      valid_time_until: null,
      valid_from: null,
      valid_to: null,
      display_order: rules.filter(r => r._status !== 'removed').length,
      featured: false,
      active: true,
      channels: ['online', 'admin'],
      flow_type: null,
      location_ids: null,
      _status: 'added',
      _customLabel: false,
      _translations: {},
      _labelDuration: { type: 'hours', value: 1 },
      _staleLangs: [],
    });
    setActiveTranslationTab(defLang);
  }

  function startEditRule(rule: PricingRuleChange) {
    setEditingRule({
      id: rule.id,
      product_type_id: rule.product_type_id,
      duration_type: rule.duration_type,
      duration_value: rule.duration_value,
      duration_label: rule.duration_label,
      price: rule.price,
      currency: rule.currency || currency,
      size_specific: rule.size_specific,
      size: rule.size,
      valid_days_of_week: rule.valid_days_of_week,
      valid_time_from: rule.valid_time_from,
      valid_time_until: rule.valid_time_until,
      valid_from: rule.valid_from,
      valid_to: rule.valid_to,
      display_order: rule.display_order,
      featured: rule.featured,
      active: rule.active,
      channels: rule.channels || ['online', 'admin'],
      flow_type: rule.flow_type || null,
      location_ids: rule.location_ids,
      _status: rule._status,
      _customLabel: rule._customLabel,
      _translations: rule._translations ? { ...rule._translations } : {},
      // Stored labels were written for the stored duration.
      _labelDuration: { type: rule.duration_type, value: rule.duration_value },
      _staleLangs: [],
    });
    setActiveTranslationTab(defLang);
  }

  function cancelEdit() {
    setEditingRule(null);
  }

  function saveRule() {
    if (!editingRule) return;

    if (!editingRule.duration_type || editingRule.price === undefined) {
      setError(t('pricingRules.durationAndPriceRequired'));
      return;
    }

    setError(null);

    const ruleData = buildRuleData(editingRule);

    let updated: PricingRuleChange[];

    if (editingRule.id) {
      // Update existing rule
      updated = rules.map(r =>
        r.id === editingRule.id ? ruleData : r
      );
    } else {
      // Add new rule with temporary ID
      updated = [...rules, { ...ruleData, id: `temp-${Date.now()}` }];
    }

    setRules(updated);
    onChange(updated);
    setEditingRule(null);
  }

  function handleRemoveRule(ruleId: string) {
    const updated = rules.map(rule => {
      if (rule.id === ruleId) {
        // If it was newly added, remove it completely (nothing to undo —
        // it never hit the database)
        if (rule._status === 'added') {
          return null;
        }
        // If it's existing, mark as removed so it stays visible as a
        // "pending deletion" row until the parent form is saved.
        return { ...rule, _status: 'removed' as const };
      }
      return rule;
    }).filter(Boolean) as PricingRuleChange[];

    // Auto-collapse any pending-delete row — users shouldn't interact with it
    setExpandedRules(prev => {
      const next = new Set(prev);
      next.delete(ruleId);
      return next;
    });

    setRules(updated);
    onChange(updated);
  }

  function handleRestoreRule(ruleId: string) {
    const updated = rules.map(rule => {
      if (rule.id === ruleId && rule._status === 'removed') {
        return { ...rule, _status: 'existing' as const };
      }
      return rule;
    });

    setRules(updated);
    onChange(updated);
  }

  function toggleExpand(id: string) {
    setExpandedRules(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  function toggleDayOfWeek(day: number) {
    if (!editingRule) return;
    const current = editingRule.valid_days_of_week || [];
    const updated = current.includes(day)
      ? current.filter(d => d !== day)
      : [...current, day].sort();
    setEditingRule({ ...editingRule, valid_days_of_week: updated.length ? updated : null });
  }

  function toggleChannel(ch: string) {
    if (!editingRule) return;
    const current = editingRule.channels || ['online', 'admin'];
    const updated = current.includes(ch)
      ? current.filter(c => c !== ch)
      : [...current, ch];
    // Ensure at least one channel is selected
    if (updated.length === 0) return;
    setEditingRule({ ...editingRule, channels: updated });
  }

  function toggleLocationId(locId: string) {
    if (!editingRule) return;
    const current = editingRule.location_ids || [];
    const updated = current.includes(locId)
      ? current.filter(id => id !== locId)
      : [...current, locId];
    setEditingRule({ ...editingRule, location_ids: updated.length ? updated : null });
  }

  function formatDuration(rule: PricingRuleChange): string {
    const typeKey = DURATION_TYPE_KEYS[rule.duration_type || ''];
    const type = typeKey ? t(`pricingRules.${typeKey}`) : rule.duration_type;
    if (rule.duration_value) {
      return `${rule.duration_value} ${type}`;
    }
    return type || '';
  }

  function formatPrice(price: number | undefined | null): string {
    if (price === undefined || price === null) return '';
    return `${currency} ${price.toFixed(2)}`;
  }

  // All rules including those marked for deletion — pending-delete rows
  // stay visible (dimmed) so the user can see what will be removed on save
  // and restore by mistake if needed.
  const visibleRules = rules;
  // Counts exclude pending-delete rows so the header reflects what will
  // actually remain after save.
  const activeRuleCount = rules.filter(rule => rule._status !== 'removed').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-xl">
        <Loader2 className="w-6 h-6 text-primary animate-spin" />
        <span className="ml-md text-secondary">{t('pricingRules.loadingRules')}</span>
      </div>
    );
  }

  return (
    <div className="space-y-lg">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-primary">{t('pricingRules.title')}</h3>
          <p className="text-sm text-secondary">
            {t('pricingRules.rulesConfigured', { count: activeRuleCount })}
          </p>
        </div>
        {!editingRule && (
          <button onClick={startNewRule} className="btn btn-primary">
            <Plus className="w-4 h-4" />
            {t('pricingRules.addRule')}
          </button>
        )}
      </div>

      {error && (
        <div className="p-md bg-red-50 border border-red-200 rounded-lg flex items-center gap-sm text-red-700">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span className="text-sm">{error}</span>
          <button onClick={() => setError(null)} className="ml-auto text-red-500 hover:text-red-700">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {editingRule && (
        <div className="card border-2 border-blue p-lg space-y-lg">
          <h4 className="font-medium text-primary">
            {editingRule.id && editingRule._status === 'existing' ? t('pricingRules.editRule') : t('pricingRules.newRule')}
          </h4>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-md">
            <div>
              <label className="block text-sm font-medium text-primary mb-sm">
                {t('pricingRules.durationType')} <span className="text-danger">*</span>
              </label>
              <select
                value={editingRule.duration_type || ''}
                onChange={(e) => applyDurationChange(editingRule, { duration_type: e.target.value })}
                className="input w-full"
              >
                {DURATION_TYPES.map(dt => (
                  <option key={dt.value} value={dt.value}>{t(`pricingRules.${DURATION_TYPE_KEYS[dt.value]}`)}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-primary mb-sm">
                {t('pricingRules.durationValue')}
              </label>
              <input
                type="number"
                value={editingRule.duration_value ?? ''}
                onChange={(e) => {
                  const raw = e.target.value;
                  if (raw === '') {
                    applyDurationChange(editingRule, { duration_value: null });
                    return;
                  }
                  // Only accept integers — strip any decimal portion that
                  // may have been pasted or entered via browser spinners.
                  const parsed = parseInt(raw, 10);
                  if (!Number.isFinite(parsed)) return;
                  applyDurationChange(editingRule, { duration_value: parsed });
                }}
                onKeyDown={(e) => {
                  // Prevent typing decimal separators directly.
                  if (e.key === '.' || e.key === ',' || e.key === 'e' || e.key === 'E') {
                    e.preventDefault();
                  }
                }}
                min="0"
                step="1"
                inputMode="numeric"
                placeholder="e.g., 1, 2, 4"
                className="input w-full"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-primary mb-sm">
                {t('pricingRules.price')} <span className="text-danger">*</span>
              </label>
              <div className="flex items-center gap-sm">
                <span className="text-sm font-medium text-secondary">{currency}</span>
                <input
                  type="number"
                  value={editingRule.price ?? ''}
                  onChange={(e) => setEditingRule({
                    ...editingRule,
                    price: parseFloat(e.target.value) || 0
                  })}
                  min="0"
                  step="0.01"
                  className="input flex-1"
                  placeholder="0.00"
                />
              </div>
            </div>
          </div>

          {/* Duration Label section */}
          <div className="border border-primary rounded-lg p-md space-y-sm">
            <div className="flex items-center justify-between">
              <label className="block text-sm font-medium text-primary">
                {t('pricingRules.durationLabel')}
              </label>
              <label className="flex items-center gap-xs text-sm text-secondary cursor-pointer">
                <input
                  type="checkbox"
                  checked={editingRule._customLabel || false}
                  onChange={(e) => {
                    const goingCustom = e.target.checked;
                    if (goingCustom) {
                      // Switching to custom: pre-fill with current auto-generated values
                      const autoLabel = generateDurationLabel(
                        editingRule.duration_type || 'hours',
                        editingRule.duration_value,
                        defLang
                      );
                      const autoTranslations = generateAutoTranslations(
                        editingRule.duration_type || 'hours',
                        editingRule.duration_value,
                        languages,
                        defLang,
                        editingRule._translations
                      );
                      setEditingRule({
                        ...editingRule,
                        _customLabel: true,
                        duration_label: autoLabel,
                        _translations: autoTranslations,
                        // Pre-filled from the current duration, so nothing is stale.
                        _labelDuration: {
                          type: editingRule.duration_type,
                          value: editingRule.duration_value,
                        },
                        _staleLangs: [],
                      });
                    } else {
                      // Switching back to auto: regenerate. Languages without
                      // locale data keep their stored value — that is what stays
                      // in the DB, so local state must not diverge from it.
                      setEditingRule({
                        ...editingRule,
                        _customLabel: false,
                        _translations: generateAutoTranslations(
                          editingRule.duration_type || 'hours',
                          editingRule.duration_value,
                          languages,
                          defLang,
                          editingRule._translations
                        ),
                        _staleLangs: [],
                      });
                    }
                  }}
                  className="rounded"
                />
                <Pencil className="w-3 h-3" />
                {t('pricingRules.customizeLabel')}
              </label>
            </div>

            {!editingRule._customLabel ? (
              /* Auto-generated label preview */
              <div>
                <div className="flex items-center gap-sm">
                  <span className="input w-full bg-tertiary text-secondary" style={{ display: 'block', padding: '8px 12px' }}>
                    {generateDurationLabel(editingRule.duration_type || 'hours', editingRule.duration_value, defLang)}
                  </span>
                  <span className="text-xs text-secondary whitespace-nowrap">({t('pricingRules.autoGenerated')})</span>
                </div>
                {hasMultipleLanguages && (
                  <div className="flex flex-wrap gap-x-md gap-y-xs mt-sm">
                    {languages.filter(l => l.code !== defLang).map(lang => {
                      // Without locale data there is nothing to generate, so show
                      // the stored translation rather than the English fallback.
                      const preview = hasGeneratedLabels(lang.code)
                        ? generateDurationLabel(editingRule.duration_type || 'hours', editingRule.duration_value, lang.code)
                        : editingRule._translations?.[lang.code]?.duration_label;
                      return (
                        <span key={lang.code} className="text-xs text-secondary">
                          {lang.flag} {preview || t('pricingRules.notTranslated')}
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>
            ) : (
              /* Custom label with language tabs */
              <div className="space-y-sm">
                {staleLangs.length > 0 && (
                  <div className="p-sm bg-warning/10 border-l-4 border-warning rounded-lg">
                    <div className="flex items-start gap-sm">
                      <AlertCircle className="w-4 h-4 text-warning flex-shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-warning">{t('pricingRules.labelsStale')}</p>
                        <p className="text-xs text-secondary mt-xs">
                          {staleLangs
                            .map(code => languages.find(l => l.code === code)?.label || code.toUpperCase())
                            .join(', ')}
                        </p>
                      </div>
                      {staleLangs.some(hasGeneratedLabels) && (
                        <button
                          type="button"
                          onClick={() => regenerateAllLabels(editingRule)}
                          className="btn btn-secondary btn-sm whitespace-nowrap"
                        >
                          {t('pricingRules.regenerateAll')}
                        </button>
                      )}
                    </div>
                  </div>
                )}
                {hasMultipleLanguages ? (
                  <>
                    <div className="flex flex-wrap gap-xs overflow-x-auto">
                      {languages.map(lang => (
                        <button
                          key={lang.code}
                          type="button"
                          onClick={() => setActiveTranslationTab(lang.code)}
                          className={`px-md py-xs text-sm rounded-md border transition-colors whitespace-nowrap ${
                            activeTranslationTab === lang.code
                              ? 'bg-blue text-white border-blue'
                              : 'bg-tertiary text-secondary border-primary hover:bg-secondary'
                          }`}
                        >
                          {lang.flag} {lang.label}
                          {staleLangs.includes(lang.code) && (
                            <span className="ml-xs" title={t('pricingRules.staleLabel')}>!</span>
                          )}
                        </button>
                      ))}
                    </div>
                    <div className="flex items-center gap-sm">
                      {activeTranslationTab === defLang ? (
                        <input
                          type="text"
                          value={editingRule.duration_label || ''}
                          onChange={(e) => setEditingRule({
                            ...editingRule,
                            duration_label: e.target.value,
                            _staleLangs: clearStale(editingRule, defLang),
                          })}
                          className="input w-full"
                          placeholder={generateDurationLabel(editingRule.duration_type || 'hours', editingRule.duration_value, defLang)}
                        />
                      ) : (
                        <input
                          type="text"
                          value={editingRule._translations?.[activeTranslationTab]?.duration_label || ''}
                          onChange={(e) => setEditingRule({
                            ...editingRule,
                            _translations: {
                              ...editingRule._translations,
                              [activeTranslationTab]: { duration_label: e.target.value },
                            },
                            _staleLangs: clearStale(editingRule, activeTranslationTab),
                          })}
                          className="input w-full"
                          placeholder={hasGeneratedLabels(activeTranslationTab)
                            ? generateDurationLabel(editingRule.duration_type || 'hours', editingRule.duration_value, activeTranslationTab)
                            : t('pricingRules.notTranslated')}
                        />
                      )}
                      {staleLangs.includes(activeTranslationTab) && hasGeneratedLabels(activeTranslationTab) && (
                        <button
                          type="button"
                          onClick={() => regenerateLabel(editingRule, activeTranslationTab)}
                          className="btn btn-secondary btn-sm whitespace-nowrap"
                        >
                          {t('pricingRules.regenerate')}
                        </button>
                      )}
                    </div>
                  </>
                ) : (
                  /* Single language: just show the editable field */
                  <div className="flex items-center gap-sm">
                    <input
                      type="text"
                      value={editingRule.duration_label || ''}
                      onChange={(e) => setEditingRule({
                        ...editingRule,
                        duration_label: e.target.value,
                        _staleLangs: clearStale(editingRule, defLang),
                      })}
                      className="input w-full"
                      placeholder={generateDurationLabel(editingRule.duration_type || 'hours', editingRule.duration_value, defLang)}
                    />
                    {staleLangs.includes(defLang) && hasGeneratedLabels(defLang) && (
                      <button
                        type="button"
                        onClick={() => regenerateLabel(editingRule, defLang)}
                        className="btn btn-secondary btn-sm whitespace-nowrap"
                      >
                        {t('pricingRules.regenerate')}
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-md">
            <div>
              <label className="block text-sm font-medium text-primary mb-sm">
                {t('pricingRules.featured')}
              </label>
              <select
                value={editingRule.featured ? 'true' : 'false'}
                onChange={(e) => setEditingRule({ ...editingRule, featured: e.target.value === 'true' })}
                className="input w-full"
              >
                <option value="false">{t('common.no')}</option>
                <option value="true">{t('common.yes')}</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-primary mb-sm">
                {t('pricingRules.standardVsExtension')}
              </label>
              <select
                value={editingRule.active !== false ? 'true' : 'false'}
                onChange={(e) => setEditingRule({ ...editingRule, active: e.target.value === 'true' })}
                className="input w-full"
              >
                <option value="true">{t('pricingRules.standard')}</option>
                <option value="false">{t('pricingRules.onlyForExtensions')}</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-primary mb-sm">
                {t('pricingRules.availableIn')}
              </label>
              <div className="flex flex-wrap gap-sm">
                {CHANNELS.map(ch => (
                  <button
                    key={ch.value}
                    type="button"
                    onClick={() => toggleChannel(ch.value)}
                    className={`px-md py-sm text-sm rounded-md border transition-colors ${
                      (editingRule.channels || ['online', 'admin']).includes(ch.value)
                        ? 'bg-blue text-white border-blue'
                        : 'bg-tertiary text-secondary border-primary hover:bg-secondary'
                    }`}
                  >
                    {t(`pricingRules.${CHANNEL_KEYS[ch.value]}`)}
                  </button>
                ))}
              </div>
              <p className="text-xs text-secondary mt-xs">{t('pricingRules.selectAvailability')}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-primary mb-sm">
                {t('pricingRules.flowType', 'Booking Flow')}
              </label>
              <div className="flex flex-wrap gap-sm">
                {FLOW_TYPES.map(ft => (
                  <button
                    key={ft.value}
                    type="button"
                    onClick={() => setEditingRule({ ...editingRule, flow_type: ft.value || null })}
                    className={`px-md py-sm text-sm rounded-md border transition-colors ${
                      (editingRule.flow_type || '') === ft.value
                        ? 'bg-blue text-white border-blue'
                        : 'bg-tertiary text-secondary border-primary hover:bg-secondary'
                    }`}
                  >
                    {t(`pricingRules.flowType_${ft.value || 'both'}`, ft.label)}
                  </button>
                ))}
              </div>
              <p className="text-xs text-secondary mt-xs">
                {t('pricingRules.flowTypeHelp', '"Both flows" = shown everywhere. "Start Now" = only for immediate rentals. "Reservation" = only for advance bookings.')}
              </p>
            </div>
          </div>

          <div className="border-t border-primary pt-md">
            <h5 className="text-sm font-medium text-primary mb-md">{t('pricingRules.validityOptional')}</h5>
            <div className="space-y-md">
              <div>
                <label className="block text-sm text-secondary mb-sm">{t('pricingRules.validDaysOfWeek')}</label>
                <div className="flex flex-wrap gap-sm">
                  {DAYS_OF_WEEK.map(day => (
                    <button
                      key={day.value}
                      type="button"
                      onClick={() => toggleDayOfWeek(day.value)}
                      className={`px-md py-sm text-sm rounded-md border transition-colors ${
                        (editingRule.valid_days_of_week || []).includes(day.value)
                          ? 'bg-blue text-white border-blue'
                          : 'bg-tertiary text-secondary border-primary hover:bg-secondary'
                      }`}
                    >
                      {t(`pricingRules.${DAY_KEYS[day.value]}`)}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-secondary mt-xs">{t('pricingRules.leaveEmptyAllDays')}</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-md">
                <div>
                  <label className="block text-sm text-secondary mb-sm">{t('pricingRules.validFromDate')}</label>
                  <input
                    type="date"
                    value={editingRule.valid_from || ''}
                    onChange={(e) => setEditingRule({ ...editingRule, valid_from: e.target.value || null })}
                    className="input w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm text-secondary mb-sm">{t('pricingRules.validToDate')}</label>
                  <input
                    type="date"
                    value={editingRule.valid_to || ''}
                    onChange={(e) => setEditingRule({ ...editingRule, valid_to: e.target.value || null })}
                    className="input w-full"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="border-t border-primary pt-md">
            <h5 className="text-sm font-medium text-primary mb-md">{t('pricingRules.locationRestrictions')}</h5>
            <div className="flex flex-wrap gap-sm">
              {locations.map(loc => (
                <button
                  key={loc.id}
                  type="button"
                  onClick={() => toggleLocationId(loc.id)}
                  className={`px-md py-sm text-sm rounded-md border transition-colors ${
                    (editingRule.location_ids || []).includes(loc.id)
                      ? 'bg-blue text-white border-blue'
                      : 'bg-tertiary text-secondary border-primary hover:bg-secondary'
                  }`}
                >
                  {loc.location_name}
                </button>
              ))}
            </div>
            <p className="text-xs text-secondary mt-xs">{t('pricingRules.leaveEmptyAllLocations')}</p>
          </div>

          <div className="flex items-center gap-md pt-md border-t border-primary">
            <button onClick={cancelEdit} className="btn btn-ghost">
              <X className="w-4 h-4" />
              {t('common.cancel')}
            </button>
            <button onClick={saveRule} className="btn btn-primary">
              <Check className="w-4 h-4" />
              {t('pricingRules.apply')}
            </button>
          </div>
        </div>
      )}

      {activeRuleCount === 0 && !editingRule ? (
        <div className="text-center py-xl border-2 border-dashed border-primary rounded-lg">
          <AlertCircle className="w-12 h-12 text-secondary mx-auto mb-md" />
          <p className="text-secondary mb-md">{t('pricingRules.noRulesConfigured')}</p>
          <button onClick={startNewRule} className="btn btn-primary">
            <Plus className="w-4 h-4" />
            {t('pricingRules.addFirstRule')}
          </button>
        </div>
      ) : (
        !editingRule && (
          <div className="space-y-sm">
            {visibleRules.map(rule => {
              const isExpanded = expandedRules.has(rule.id!);
              const ruleId = rule.id || '';
              const isPendingDelete = rule._status === 'removed';

              return (
                <div
                  key={ruleId}
                  className={`border rounded-lg overflow-hidden transition-colors ${
                    isPendingDelete
                      ? 'border-red-300 bg-red-50 opacity-60'
                      : rule._status === 'added'
                        ? 'border-green-300 bg-green-50'
                        : rule.active
                          ? 'border-primary bg-primary'
                          : 'border-amber-300 bg-amber-50'
                  }`}
                >
                  <div
                    className={`flex items-center gap-md p-md ${
                      isPendingDelete ? '' : 'cursor-pointer hover:bg-secondary'
                    }`}
                    onClick={isPendingDelete ? undefined : () => toggleExpand(ruleId)}
                  >
                    <button className="text-secondary" disabled={isPendingDelete}>
                      {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                    </button>
                    <div className={`flex-1 grid grid-cols-1 sm:grid-cols-3 gap-sm sm:gap-md ${isPendingDelete ? 'line-through' : ''}`}>
                      <div>
                        <p className="font-medium text-primary">{formatDuration(rule)}</p>
                        {rule.duration_label && rule._customLabel && (
                          <p className="text-xs text-blue truncate">{rule.duration_label}</p>
                        )}
                        <p className="text-xs text-secondary">{t('pricingRules.duration')}</p>
                      </div>
                      <div>
                        <p className="font-medium text-primary">{formatPrice(rule.price)}</p>
                        <p className="text-xs text-secondary">{t('pricingRules.price')}</p>
                      </div>
                      <div className="flex items-center gap-sm flex-wrap">
                        {isPendingDelete && (
                          <span className="px-sm py-xs text-xs bg-red-200 text-red-800 rounded no-underline">
                            {t('pricingRules.pendingDelete', 'Will be deleted on save')}
                          </span>
                        )}
                        {!isPendingDelete && !rule.active && (
                          <span className="px-sm py-xs text-xs bg-amber-200 text-amber-800 rounded">{t('pricingRules.extensionsOnly')}</span>
                        )}
                        {!isPendingDelete && rule.active && rule.channels && !rule.channels.includes('online') && (
                          <span className="px-sm py-xs text-xs bg-purple-200 text-purple-800 rounded">{t('pricingRules.adminOnly')}</span>
                        )}
                        {!isPendingDelete && rule.active && rule.channels && !rule.channels.includes('admin') && (
                          <span className="px-sm py-xs text-xs bg-teal-200 text-teal-800 rounded">{t('pricingRules.onlineOnly')}</span>
                        )}
                        {!isPendingDelete && rule.flow_type === 'immediate' && (
                          <span className="px-sm py-xs text-xs bg-orange-200 text-orange-800 rounded">{t('pricingRules.flowType_immediate', 'Start Now')}</span>
                        )}
                        {!isPendingDelete && rule.flow_type === 'reservation' && (
                          <span className="px-sm py-xs text-xs bg-indigo-200 text-indigo-800 rounded">{t('pricingRules.flowType_reservation', 'Reservation')}</span>
                        )}
                        {!isPendingDelete && rule.featured && (
                          <span className="px-sm py-xs text-xs bg-blue/10 text-blue rounded">{t('pricingRules.featured')}</span>
                        )}
                        {!isPendingDelete && rule.size_specific && (
                          <span className="px-sm py-xs text-xs bg-gray-200 text-gray-700 rounded">{rule.size}</span>
                        )}
                        {!isPendingDelete && rule._status === 'added' && (
                          <span className="px-sm py-xs text-xs bg-green-600 text-white rounded">{t('pricingRules.new')}</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-xs" onClick={e => e.stopPropagation()}>
                      {isPendingDelete ? (
                        <button
                          onClick={() => handleRestoreRule(ruleId)}
                          className="p-sm text-blue hover:text-blue-700 hover:bg-blue/10 rounded"
                          title={t('pricingRules.restoreRule', 'Restore')}
                        >
                          <Undo2 className="w-4 h-4" />
                        </button>
                      ) : (
                        <>
                          <button
                            onClick={() => startEditRule(rule)}
                            className="p-sm text-blue hover:text-blue-700 hover:bg-blue/10 rounded"
                            title={t('pricingRules.editRule')}
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleRemoveRule(ruleId)}
                            className="p-sm text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                            title={t('pricingRules.removeRule')}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </div>

                  {isExpanded && !isPendingDelete && (
                    <div className="px-md pb-md pl-12 text-sm">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-md text-secondary">
                        {rule.duration_label && (
                          <div>
                            <p className="text-xs font-medium">{t('pricingRules.durationLabel')}</p>
                            <p>{rule.duration_label}</p>
                            {rule._customLabel && (
                              <span className="text-xs text-blue">({t('pricingRules.customized')})</span>
                            )}
                          </div>
                        )}
                        {rule.valid_days_of_week && (
                          <div>
                            <p className="text-xs font-medium">{t('pricingRules.validDays')}</p>
                            <p>{rule.valid_days_of_week.map(d => t(`pricingRules.${DAY_KEYS[d]}`)).join(', ')}</p>
                          </div>
                        )}
                        {(rule.valid_from || rule.valid_to) && (
                          <div>
                            <p className="text-xs font-medium">{t('pricingRules.validDates')}</p>
                            <p>{rule.valid_from || t('pricingRules.any')} - {rule.valid_to || t('pricingRules.any')}</p>
                          </div>
                        )}
                        {rule.location_ids && rule.location_ids.length > 0 && (
                          <div className="col-span-2">
                            <p className="text-xs font-medium">{t('pricingRules.locations')}</p>
                            <p>
                              {rule.location_ids
                                .map(id => locations.find(l => l.id === id)?.location_name || id.substring(0, 8))
                                .join(', ')}
                            </p>
                          </div>
                        )}
                        {rule.channels && (rule.channels.length < 2 || !rule.channels.includes('online') || !rule.channels.includes('admin')) && (
                          <div>
                            <p className="text-xs font-medium">{t('pricingRules.channels')}</p>
                            <p>{rule.channels.map(ch => t(`pricingRules.${CHANNEL_KEYS[ch]}`) || ch).join(', ')}</p>
                          </div>
                        )}
                        {rule.flow_type && (
                          <div>
                            <p className="text-xs font-medium">{t('pricingRules.flowType', 'Booking Flow')}</p>
                            <p>{t(`pricingRules.flowType_${rule.flow_type}`, rule.flow_type)}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )
      )}
    </div>
  );
});
